import { IPasswordSecurity } from '@/entities/admin-app/filials';
import { allowedPasswordCharactersString } from '@utils/passwordUtils';
import { z } from 'zod';
import { TFunction } from 'i18next';

const escapeForRegex = (str: string): string => {
  return str.replace(/[-\\[\]^]/g, '\\$&');
};

export const usePasswordSchema = (options: IPasswordSecurity, t: TFunction) => {
  const maxLength = 30;
  const minLength =
    options.MinLength && options.MinLength >= 8 && options.MinLength <= 30 ? options.MinLength : 8;

  return z.string().superRefine((val, ctx) => {
    if (!val || val.length < minLength || val.length > maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.minMaxLength', {
          MinLength: minLength,
          MaxLength: maxLength
        })
      });
    }

    if (val.includes(' ')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.passwordShouldNotContainSpaces')
      });
      return;
    }

    if (options.UseLatin && !/^[a-zA-Z0-9!-/:-@[-`{-~]*$/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.passwordLatinOnly')
      });
      return;
    }

    if (options.UseCapitalLetters && !/[A-Z]/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.useCapitalLetters')
      });
      return;
    }

    if (options.UseLowLetter && !/[a-z]/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.useLowLetter')
      });
      return;
    }

    if (options.UseDigital && !/\d/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('passwordReset.passwordPolicy.digital')
      });
      return;
    }

    if (options.UseAllowedSpecialCharacters) {
      const escaped = escapeForRegex(allowedPasswordCharactersString);
      const allowedClass = `a-zA-Z0-9${escaped}`;
      const invalidCharRegex = new RegExp(`[^${allowedClass}]`, 'g');

      const invalid = val.match(invalidCharRegex)?.filter((v, i, a) => a.indexOf(v) === i) || [];

      if (invalid.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('passwordReset.passwordPolicy.allowedSpecialCharacters', {
            characters: allowedPasswordCharactersString,
            symbols: ` (${invalid.join(', ')})`
          })
        });
        return; // ⛔ остановка
      }
    }

    if (options.UseSpecialCharacters) {
      const input = val || '';

      let passed = false;

      if (options.UseAllowedSpecialCharacters) {
        const escaped = escapeForRegex(allowedPasswordCharactersString);
        const regex = new RegExp(`[${escaped}]`);
        passed = regex.test(input); // хотя бы один разрешённый спецсимвол
      } else {
        passed = /[^a-zA-Z0-9]/.test(input); // хотя бы один не-буква и не-цифра
      }

      if (!passed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('passwordReset.passwordPolicy.useSpecialCharacters')
        });
        return; // ⛔ останавливаемся
      }
    }
  });
};
